import React, { useState } from 'react';
import { ChevronDown, Search, Building, Plus, CheckCircle, AlertTriangle, Clock } from 'lucide-react';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { useWorkspaceHealth } from '@/hooks/useWorkspaceHealth';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { CreateWorkspaceModal } from './CreateWorkspaceModal';

export const WorkspaceSwitcher: React.FC = () => {
  const { currentWorkspace, switchWorkspace, isOwner } = useWorkspace();
  const { overallStatus, healthScore } = useWorkspaceHealth(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // Query user workspaces with owner fallback
  const { data: workspaces = [], isLoading } = useQuery({
    queryKey: ['workspaces', user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      console.log('🔍 WorkspaceSwitcher: Fetching user workspaces');
      
      // First try regular member query
      const { data: memberData, error: memberError } = await supabase
        .from('workspace_members')
        .select(`
          workspace:workspaces(
            id,
            name,
            slug,
            organization:organizations(id, name)
          )
        `)
        .eq('user_id', user!.id)
        .eq('status', 'active');

      if (memberError) {
        console.error('❌ WorkspaceSwitcher: Member query error:', memberError);
      }

      const memberWorkspaces = (memberData ?? [])
        .map(d => d.workspace)
        .filter(Boolean);

      console.log('📊 WorkspaceSwitcher: Member workspaces:', memberWorkspaces.length);

      // Owner fallback: if list empty and user is owner, call workspace-read-all
      if (memberWorkspaces.length === 0 && isOwner) {
        console.log('🔑 WorkspaceSwitcher: Owner fallback - fetching all workspaces');
        
        try {
          const { data: ownerData, error: ownerError } = await supabase.functions.invoke(
            'workspace-read-all',
            {
              headers: {
                Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
              }
            }
          );

          if (ownerError) {
            console.error('❌ WorkspaceSwitcher: Owner fallback error:', ownerError);
            return memberWorkspaces;
          }

          console.log('✅ WorkspaceSwitcher: Owner fallback success:', ownerData?.workspaces?.length || 0);
          return ownerData?.workspaces || memberWorkspaces;
        } catch (error) {
          console.error('❌ WorkspaceSwitcher: Owner fallback exception:', error);
          return memberWorkspaces;
        }
      }

      return memberWorkspaces;
    },
  });

  const filteredWorkspaces = workspaces.filter(ws =>
    ws?.name?.toLowerCase().includes(search.toLowerCase()) ||
    ws?.slug?.toLowerCase().includes(search.toLowerCase())
  );

  const getHealthBadge = (status: string) => {
    switch (status) {
      case 'healthy':
        return <Badge variant="secondary" className="text-green-600 bg-green-50 text-xs">Healthy</Badge>;
      case 'warning':
        return <Badge variant="secondary" className="text-yellow-600 bg-yellow-50 text-xs">Warning</Badge>;
      case 'error':
        return <Badge variant="destructive" className="text-xs">Error</Badge>;
      case 'maintenance':
        return <Badge variant="secondary" className="text-blue-600 bg-blue-50 text-xs">Maintenance</Badge>;
      default:
        return null;
    }
  };

  const getHealthIcon = (status: string) => {
    switch (status) {
      case 'healthy': return <CheckCircle className="h-3 w-3 text-green-600" />;
      case 'warning': return <AlertTriangle className="h-3 w-3 text-yellow-600" />;
      case 'error': return <AlertTriangle className="h-3 w-3 text-red-600" />;
      case 'maintenance': return <Clock className="h-3 w-3 text-blue-600" />;
      default: return null;
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className="w-[240px] justify-between">
            <div className="flex items-center space-x-2">
              <Building className="h-4 w-4" />
              <span className="truncate">
                {currentWorkspace?.name || 'Select Workspace'}
              </span>
              {currentWorkspace && getHealthIcon(overallStatus)}
            </div>
            <ChevronDown className="h-4 w-4 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-80">
          <div className="p-3 border-b">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search workspaces..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
          
          <div className="max-h-60 overflow-y-auto">
            {isLoading ? (
              <div className="p-3">
                <div className="animate-pulse space-y-2">
                  <div className="h-4 bg-muted rounded w-3/4"></div>
                  <div className="h-3 bg-muted rounded w-1/2"></div>
                </div>
              </div>
            ) : filteredWorkspaces.length === 0 ? (
              <div className="p-3 text-center text-muted-foreground">
                <div className="text-sm">No workspaces found</div>
                <div className="text-xs mt-1">Create your first workspace below</div>
              </div>
            ) : (
              filteredWorkspaces.map((workspace) => (
                <DropdownMenuItem
                  key={workspace.id}
                  onClick={() => {
                    console.log('🔄 WorkspaceSwitcher: Switching to workspace:', workspace.id);
                    navigate(`/w/${workspace.id}/dashboard`);
                  }}
                  className={`cursor-pointer ${
                    currentWorkspace?.id === workspace.id ? 'bg-accent' : ''
                  }`}
                >
                  <div className="w-full">
                    <div className="flex items-center justify-between">
                      <div className="font-medium">{workspace.name}</div>
                      {currentWorkspace?.id === workspace.id && getHealthBadge(overallStatus)}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {workspace.organization?.name}
                      {currentWorkspace?.id === workspace.id && healthScore !== undefined && (
                        <span className="ml-2">Health: {healthScore}%</span>
                      )}
                    </div>
                  </div>
                </DropdownMenuItem>
              ))
            )}
          </div>

          <DropdownMenuSeparator />
          <DropdownMenuItem 
            className="cursor-pointer"
            onClick={() => setIsCreateModalOpen(true)}
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Workspace
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <CreateWorkspaceModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={(workspaceId) => {
          navigate(`/w/${workspaceId}/dashboard`);
        }}
      />
    </>
  );
};